import React, { useState, useEffect, useCallback } from 'react';
import { authApiClient } from './auth/authApiClient';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import AuthenticatedContent from './components/AuthenticatedContent';
import Login from './components/Login';
import ChunkedUploader from './utils/chunkedUploader';
import { loadConfig, getApiConfig } from './utils/configLoader';

// Component that uses MSAL hooks (only when auth is enabled)
function AppWithAuth() {
  const { instance, inProgress, accounts } = useMsal();
  return <AppContent instance={instance} inProgress={inProgress} accounts={accounts} authDisabled={false} />;
}

// Component without auth dependencies
function AppWithoutAuth() {
  return <AppContent instance={null} inProgress={null} accounts={[]} authDisabled={true} />;
}

// Main app content component that receives auth props
function AppContent({ instance, inProgress, accounts, authDisabled }) {

  const [mode, setMode] = useState('meeting_summary');
  const [prompt, setPrompt] = useState(
    "Generate a detailed summary of the meeting in the attached video recording, including discussion points and action items with owners"
  );
  const [error, setError] = useState('');
  const [authChecked, setAuthChecked] = useState(authDisabled); // Skip auth check if disabled
  const [configLoaded, setConfigLoaded] = useState(false);

  // Queue state for multiple files
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);

  // New: Processing mode state
  const [processingMode, setProcessingMode] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('processingMode');
    return saved || 'single';
  });
  const [batchQueue, setBatchQueue] = useState([]);
  const [batchResult, setBatchResult] = useState(null); // Single result for entire batch

  // Persist processing mode to localStorage
  useEffect(() => {
    localStorage.setItem('processingMode', processingMode);
  }, [processingMode]);

  // Load configuration when authentication is disabled
  useEffect(() => {
    if (authDisabled) {
      loadConfig()
        .then(() => {
          console.log('Configuration loaded successfully');
          setConfigLoaded(true);
        })
        .catch(error => {
          console.error('Failed to load configuration:', error);
          setError('Failed to load application configuration');
        });
    } else {
      // Configuration is loaded by AuthProvider when auth is enabled
      setConfigLoaded(true);
    }
  }, [authDisabled]);

  // Process a single file from the queue (processing only, assumes already uploaded)
  // Define this BEFORE the useEffect that uses it
  const processSingleFile = useCallback(async (queueItem) => {
    const fileId = queueItem.id;
    const fileName = queueItem.fileName;
    const serverFilePath = queueItem.serverFilePath;
    const abortSignal = queueItem.abortController.signal;

    // Validate that file has been uploaded
    if (!serverFilePath) {
      console.error('Cannot process: file not uploaded yet');
      setUploadQueue(prev => prev.map(item =>
        item.id === fileId ? {
          ...item,
          status: 'failed',
          error: 'File not uploaded. Please upload first.'
        } : item
      ));
      return;
    }

    // Update status to processing
    setUploadQueue(prev => prev.map(item =>
      item.id === fileId ? { ...item, status: 'processing' } : item
    ));

    const startTime = Date.now();

    // Check if already aborted
    if (abortSignal.aborted) {
      setUploadQueue(prev => prev.map(item =>
        item.id === fileId ? { ...item, status: 'cancelled' } : item
      ));
      return;
    }

    try {
      console.log(`Starting processing of ${fileName}`);

      // Send processing request
      const apiConfig = getApiConfig();
      const response = await authApiClient.post(
        apiConfig.videoProcessingEndpoint,
        {
          file_path: serverFilePath,
          filename: fileName,
          prompt: prompt
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 3600000, // 60 minutes timeout
          signal: abortSignal // Support cancellation
        }
      );

      // Handle success with additional error checking
      if (response && response.data && response.data.success) {
        console.log('Processing successful for', fileName);
        const processingTime = ((Date.now() - startTime) / 1000).toFixed(2);

        // Update queue item as completed
        setUploadQueue(prev => prev.map(item =>
          item.id === fileId ? {
            ...item,
            status: 'completed',
            result: response.data.content,
            processingTime: `${processingTime}s`
          } : item
        ));
      } else {
        const errorMessage = response?.data?.message || 'Processing failed';
        console.error('Processing failed:', errorMessage);

        // Update queue item as failed
        setUploadQueue(prev => prev.map(item =>
          item.id === fileId ? {
            ...item,
            status: 'failed',
            error: errorMessage
          } : item
        ));
      }
    } catch (err) {
      console.error('Error processing video:', err);

      // Check if error is due to cancellation
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED' || abortSignal.aborted) {
        console.log('Video processing was cancelled by user');
        setUploadQueue(prev => prev.map(item =>
          item.id === fileId ? {
            ...item,
            status: 'cancelled',
            error: 'Processing cancelled by user'
          } : item
        ));
        return;
      }

      const errorMessage = err.response?.data?.message ||
        'Failed to process the video. Please try again or use a shorter video.';

      // Update queue item as failed
      setUploadQueue(prev => prev.map(item =>
        item.id === fileId ? {
          ...item,
          status: 'failed',
          error: errorMessage
        } : item
      ));
    }
  }, [prompt]); // Add prompt as dependency since it's used in the function

  // Continuous queue processor - monitors queue and auto-processes videos marked as ready
  useEffect(() => {
    // Only process in single mode (batch mode has its own handler)
    if (processingMode !== 'single' || !configLoaded) {
      return;
    }

    // Find items that are uploaded and marked as ready to process
    const readyItems = uploadQueue.filter(item =>
      item.status === 'uploaded' && item.readyToProcess === true
    );

    // Count currently processing items
    const processingItems = uploadQueue.filter(item => item.status === 'processing');

    // Max 2 concurrent videos
    const MAX_PARALLEL = 2;
    const availableSlots = MAX_PARALLEL - processingItems.length;

    // If there are ready items and available slots, process them
    if (readyItems.length > 0 && availableSlots > 0) {
      const itemsToProcess = readyItems.slice(0, availableSlots);

      console.log(`Processing ${itemsToProcess.length} videos (${processingItems.length} already processing)`);

      // Process each item
      itemsToProcess.forEach(queueItem => {
        processSingleFile(queueItem);
      });
    }
  }, [uploadQueue, processingMode, configLoaded, processSingleFile]);
  
  const handleVideoSelect = (files) => {
    // Handle both single file and multiple files
    const fileArray = Array.isArray(files) ? files : [files];

    // Check batch size limit
    if (processingMode === 'batch') {
      const totalAfterAdd = batchQueue.length + fileArray.length;
      if (totalAfterAdd > 10) {
        setError(`Batch limit is 10 videos. You can only add ${10 - batchQueue.length} more video(s).`);
        return;
      }
    }

    // Create queue items with new structure
    const queueItems = fileArray.map((file, index) => ({
      id: Date.now() + index,
      file: file,
      fileName: file.name,
      status: 'uploading', // uploading, uploaded, processing, completed, failed, cancelled
      uploadProgress: 0,
      progress: null, // Processing progress
      result: null,
      error: null,
      processingTime: null,
      abortController: new AbortController(), // For cancellation support
      readyToProcess: false, // User must explicitly trigger processing
      batchOrder: null, // For batch mode
      batchId: null, // For batch processing
      serverFilePath: null // Set after upload completes
    }));

    // Add to appropriate queue based on mode
    if (processingMode === 'single') {
      setUploadQueue(prev => [...prev, ...queueItems]);
    } else {
      // Batch mode - set batch order
      const currentBatchSize = batchQueue.length;
      const itemsWithOrder = queueItems.map((item, index) => ({
        ...item,
        batchOrder: currentBatchSize + index
      }));
      setBatchQueue(prev => [...prev, ...itemsWithOrder]);
    }

    setError('');

    // Start uploading immediately (but not processing)
    queueItems.forEach(item => {
      uploadSingleFile(item);
    });
  };

  // Upload a single file (separate from processing)
  const uploadSingleFile = async (queueItem) => {
    if (!queueItem || !queueItem.file) {
      console.error('Invalid queue item or missing file:', queueItem);
      return;
    }

    const fileId = queueItem.id;
    const file = queueItem.file;
    const abortSignal = queueItem.abortController?.signal;
    const isInBatch = processingMode === 'batch';

    const updateQueue = isInBatch ? setBatchQueue : setUploadQueue;

    // Check if already aborted
    if (abortSignal && abortSignal.aborted) {
      updateQueue(prev => prev.map(item =>
        item.id === fileId ? { ...item, status: 'cancelled' } : item
      ));
      return;
    }

    try {
      const fileSize = file.size;
      const fileSizeMB = fileSize / (1024 * 1024);

      console.log(`Starting upload of ${file.name} (${fileSizeMB.toFixed(2)} MB)`);

      // Create chunked uploader with runtime config
      const apiConfigForUpload = getApiConfig();
      const uploader = new ChunkedUploader(file, (progress) => {
        console.log(`Upload progress: ${progress}%`);
        // Update upload progress
        updateQueue(prev => prev.map(item =>
          item.id === fileId ? { ...item, uploadProgress: progress } : item
        ));
      }, apiConfigForUpload.chunkedUploadEndpoint);

      // Start the chunked upload
      console.log('Starting chunked upload process...');
      const chunkUploadResult = await uploader.uploadFile();
      console.log('Upload result:', chunkUploadResult);

      if (!chunkUploadResult.success) {
        throw new Error('Chunked upload failed');
      }

      console.log('Chunked upload complete');

      // Update status to uploaded (NOT processing)
      updateQueue(prev => prev.map(item =>
        item.id === fileId ? {
          ...item,
          status: 'uploaded',
          uploadProgress: 100,
          serverFilePath: chunkUploadResult.file_path,
          readyToProcess: false // Explicitly require user action
        } : item
      ));

    } catch (uploadError) {
      console.error('Chunked upload error:', uploadError);

      // Check if error is due to cancellation
      if (uploadError.name === 'CanceledError' || uploadError.code === 'ERR_CANCELED' || abortSignal.aborted) {
        console.log('Upload was cancelled by user');
        updateQueue(prev => prev.map(item =>
          item.id === fileId ? {
            ...item,
            status: 'cancelled',
            error: 'Upload cancelled by user'
          } : item
        ));
        return;
      }

      // Update as failed
      updateQueue(prev => prev.map(item =>
        item.id === fileId ? {
          ...item,
          status: 'failed',
          error: uploadError.message || 'Upload failed'
        } : item
      ));
    }
  };

  // Start processing a single video
  const startSingleVideoProcessing = (videoId) => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    console.log(`Marking video ${videoId} as ready to process`);

    // Set readyToProcess flag - the useEffect will pick it up
    setUploadQueue(prev => prev.map(item =>
      item.id === videoId ? { ...item, readyToProcess: true } : item
    ));

    setError('');
  };

  // Move batch item up or down
  const moveBatchItem = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === batchQueue.length - 1) return;

    setBatchQueue(prev => {
      const newQueue = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;

      // Swap items
      [newQueue[index], newQueue[newIndex]] = [newQueue[newIndex], newQueue[index]];

      // Update batchOrder
      return newQueue.map((item, i) => ({ ...item, batchOrder: i }));
    });
  };

  // Remove item from batch
  const removeFromBatch = (videoId) => {
    const item = batchQueue.find(q => q.id === videoId);

    // If currently uploading or processing, cancel it first
    if (item && ['uploading', 'processing'].includes(item.status) && item.abortController) {
      item.abortController.abort();
    }

    // Remove from batch and reorder
    setBatchQueue(prev =>
      prev.filter(item => item.id !== videoId)
        .map((item, i) => ({ ...item, batchOrder: i }))
    );
  };

  // Process entire batch
  const processBatch = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    if (batchQueue.length === 0) {
      setError('Please add videos to the batch first');
      return;
    }

    // Check all videos are uploaded
    const notUploaded = batchQueue.filter(v => v.status !== 'uploaded');
    if (notUploaded.length > 0) {
      setError('Please wait for all videos to finish uploading');
      return;
    }

    setError('');
    const batchId = `batch_${Date.now()}`;

    console.log(`Processing batch of ${batchQueue.length} videos`);

    // Mark all as processing
    setBatchQueue(prev => prev.map(v => ({
      ...v,
      status: 'processing',
      batchId: batchId
    })));

    try {
      // Collect all file paths in order
      const videoPaths = batchQueue.map(v => ({
        file_path: v.serverFilePath,
        filename: v.fileName,
        order: v.batchOrder
      }));

      // Single API call for batch
      const apiConfig = getApiConfig();
      const response = await authApiClient.post(
        `${apiConfig.chunkedUploadEndpoint}/api/process-batch`,
        {
          videos: videoPaths,
          prompt: prompt,
          batch_id: batchId
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 7200000 // 120 minutes timeout for batch
        }
      );

      if (response && response.data && response.data.success) {
        console.log('Batch processing successful');
        const processingTime = response.data.processing_time || 'N/A';

        // Store batch result
        setBatchResult({
          content: response.data.content,
          videosProcessed: batchQueue.length,
          chunksProcessed: response.data.chunks_processed || 0,
          processingTime: processingTime
        });

        // Update all videos as completed
        setBatchQueue(prev => prev.map(v => ({
          ...v,
          status: 'completed',
          processingTime: processingTime
        })));
      } else {
        throw new Error(response?.data?.message || 'Batch processing failed');
      }

    } catch (error) {
      console.error('Batch processing error:', error);

      const errorMessage = error.response?.data?.error || error.message || 'Batch processing failed';
      setError(errorMessage);

      // Mark all as failed
      setBatchQueue(prev => prev.map(v => ({
        ...v,
        status: 'failed',
        error: errorMessage
      })));
    }
  };

  // Cancel/Stop processing for a specific file
  const handleCancelProcessing = (fileId) => {
    const item = uploadQueue.find(q => q.id === fileId);
    if (item && item.abortController) {
      console.log(`Cancelling processing for: ${item.fileName}`);
      item.abortController.abort();
      // Status will be updated in processSingleFile's catch block
    }
  };

  // Retry a cancelled or failed file
  const handleRetryProcessing = async (fileId) => {
    console.log(`Retrying file: ${fileId}`);

    // Check which queue the item is in
    const isInBatch = batchQueue.some(q => q.id === fileId);
    const updateQueue = isInBatch ? setBatchQueue : setUploadQueue;
    const queue = isInBatch ? batchQueue : uploadQueue;

    // Find the item
    const item = queue.find(q => q.id === fileId);
    if (!item) return;

    // Determine retry action based on status
    if (item.status === 'failed' && !item.serverFilePath) {
      // Upload failed - retry upload
      console.log('Retrying upload...');
      updateQueue(prev => prev.map(i =>
        i.id === fileId ? {
          ...i,
          status: 'uploading',
          error: null,
          uploadProgress: 0,
          abortController: new AbortController()
        } : i
      ));

      // Re-upload
      const updatedItem = {
        ...item,
        status: 'uploading',
        error: null,
        uploadProgress: 0,
        abortController: new AbortController()
      };
      await uploadSingleFile(updatedItem);

    } else if (item.status === 'failed' && item.serverFilePath) {
      // Processing failed - retry processing
      console.log('Retrying processing...');
      if (isInBatch) {
        // In batch mode, retry entire batch
        setError('Cannot retry individual videos in batch mode. Please retry the entire batch.');
      } else {
        // In single mode, retry this video
        setUploadQueue(prev => prev.map(i =>
          i.id === fileId ? {
            ...i,
            status: 'uploaded',
            error: null,
            readyToProcess: true,
            abortController: new AbortController()
          } : i
        ));
      }
    }
  };

  // Remove file from queue completely
  const handleRemoveFromQueue = (fileId) => {
    const item = uploadQueue.find(q => q.id === fileId);

    // If currently processing, cancel it first
    if (item && item.status === 'processing' && item.abortController) {
      item.abortController.abort();
    }

    // Remove from queue
    setUploadQueue(prev => prev.filter(item => item.id !== fileId));
  };

  // Clear entire queue (mode-aware)
  const handleClearQueue = () => {
    if (processingMode === 'single') {
      // Cancel all processing items in single queue
      uploadQueue.forEach(item => {
        if (['uploading', 'processing'].includes(item.status) && item.abortController) {
          item.abortController.abort();
        }
      });
      setUploadQueue([]);
    } else {
      // Cancel all items in batch queue
      batchQueue.forEach(item => {
        if (['uploading', 'processing'].includes(item.status) && item.abortController) {
          item.abortController.abort();
        }
      });
      setBatchQueue([]);
      setBatchResult(null);
    }

    setError('');
  };

  // Check for token in URL hash (from implicit flow) and handle auth status
  React.useEffect(() => {
    // Skip auth check if authentication is disabled
    if (authDisabled) {
      setAuthChecked(true);
      return;
    }

    const checkAuthStatus = async () => {
      try {
        // Check if we were redirected here due to token expiration
        const redirectedForLogin = sessionStorage.getItem("redirectedForLogin");
        if (redirectedForLogin === "true") {
          console.log("App: App was redirected here due to token expiration, forcing login");
          // Clear the flag immediately to prevent loops
          sessionStorage.removeItem("redirectedForLogin");
          
          // Clear remaining auth data if any
          sessionStorage.removeItem("accessToken");
          sessionStorage.removeItem("idToken");
          sessionStorage.removeItem("tokenExpiresAt");
          sessionStorage.removeItem("loginState");
          sessionStorage.removeItem("loginNonce");
          
          // Force login via the Login component
          setAuthChecked(true);
          return;
        }
        
        // First, check if there's a hash in the URL (from implicit flow redirect)
        console.log("App: Checking for hash in URL and auth status");
        const hash = window.location.hash;
        
        if (hash && (hash.includes("access_token=") || hash.includes("id_token="))) {
          console.log("App: Found token in URL hash, processing...");
          
          // Parse the hash to extract tokens
          const params = new URLSearchParams(hash.substring(1));
          const accessToken = params.get("access_token");
          const idToken = params.get("id_token");
          const state = params.get("state");
          const expiresIn = params.get("expires_in");
          // const nonce = params.get("nonce"); // Reserved for future use

          // Verify state matches what we stored
          const storedState = sessionStorage.getItem("loginState");
          // const storedNonce = sessionStorage.getItem("loginNonce"); // Reserved for future use
          
          if (state && storedState && state === storedState) {
            console.log("App: State validation successful");
            
            // Process the tokens - prioritize ID token for backend compatibility
            if (idToken) {
              console.log("App: Successfully received ID token");
              
              // Store both tokens in sessionStorage (be careful with security)
              sessionStorage.setItem("idToken", idToken);
              
              // For API calls that require it
              if (accessToken) {
                console.log("App: Also received access token");
                sessionStorage.setItem("accessToken", accessToken);
                
                // Calculate expiration time
                if (expiresIn) {
                  const expiresInSeconds = parseInt(expiresIn, 10);
                  const expiresAt = new Date().getTime() + expiresInSeconds * 1000;
                  sessionStorage.setItem("tokenExpiresAt", expiresAt.toString());
                }
              }
              
              // Clear the hash from the URL to prevent issues on refresh
              window.history.replaceState(null, document.title, window.location.pathname);
              
              // Force reload to update auth state
              window.location.reload();
              return;
            } else if (accessToken) {
              console.log("App: Only received access token (no ID token)");
              
              // Store the token in sessionStorage (be careful with security)
              sessionStorage.setItem("accessToken", accessToken);
              
              // Calculate expiration time
              if (expiresIn) {
                const expiresInSeconds = parseInt(expiresIn, 10);
                const expiresAt = new Date().getTime() + expiresInSeconds * 1000;
                sessionStorage.setItem("tokenExpiresAt", expiresAt.toString());
              }
              
              // Clear the hash from the URL to prevent issues on refresh
              window.history.replaceState(null, document.title, window.location.pathname);
              
              // Force reload to update auth state
              window.location.reload();
              return;
            }
          } else {
            console.error("App: State validation failed or missing");
          }
        }
        
        // Check if we have a stored token that's not expired
        const storedToken = sessionStorage.getItem("accessToken");
        const idToken = sessionStorage.getItem("idToken");
        const tokenExpiresAt = sessionStorage.getItem("tokenExpiresAt");
        
        if ((storedToken || idToken) && tokenExpiresAt) {
          const now = new Date().getTime();
          const expiresAt = parseInt(tokenExpiresAt, 10);
          
          if (now < expiresAt) {
            console.log("App: Found valid stored token");
            // We have a valid token, continue
            setAuthChecked(true);
            return;
          } else {
            console.log("App: Stored token expired, clearing all auth data");
            // Token expired, clear all auth data
            sessionStorage.removeItem("accessToken");
            sessionStorage.removeItem("idToken");
            sessionStorage.removeItem("tokenExpiresAt");
            sessionStorage.removeItem("loginState");
            sessionStorage.removeItem("loginNonce");
            
            // Set the redirected flag to ensure we show login screen
            sessionStorage.setItem("redirectedForLogin", "true");
            
            // Clear MSAL cache
            try {
              console.log("App: Clearing MSAL cache");
              instance.clearCache();
              await instance.logout({
                onRedirectNavigate: () => false // Don't redirect yet, wait for page reload
              });
            } catch (e) {
              console.error("App: Error clearing MSAL cache:", e);
            }
            
            // Force a reload after a brief delay to ensure we go back to login page
            setTimeout(() => {
              window.location.reload();
            }, 100);
            return;
          }
        }
        
        // Now proceed with normal MSAL account check
        if (inProgress !== InteractionStatus.Login) {
          console.log("App: No login in progress, checking MSAL accounts");
          
          // Get all accounts and active account
          const allAccounts = instance.getAllAccounts();
          console.log("App: Found MSAL accounts:", allAccounts.length);
          
          // If we have accounts but no active account, set one
          if (allAccounts.length > 0) {
            if (!instance.getActiveAccount()) {
              // Set the first account as active
              console.log("App: Setting active account from MSAL:", allAccounts[0].name);
              instance.setActiveAccount(allAccounts[0]);
            } else {
              console.log("App: MSAL active account already set:", instance.getActiveAccount().name);
            }
          } else {
            console.log("App: No MSAL accounts found");
          }
        }
        
        // Mark auth check as complete
        setAuthChecked(true);
      } catch (error) {
        console.error("App: Error checking auth status:", error);
        setAuthChecked(true); // Mark as checked even on error
      }
    };
    
    checkAuthStatus();
  }, [instance, inProgress, accounts, authDisabled]);

  // Check if we have a manually stored token (from implicit flow)
  const isManuallyAuthenticated = () => {
    try {
      // Check for ID token first (preferred for backend)
      const idToken = sessionStorage.getItem("idToken");
      if (idToken) {
        return true;
      }
      
      // Fall back to checking access token
      const accessToken = sessionStorage.getItem("accessToken");
      const expiresAt = sessionStorage.getItem("tokenExpiresAt");
      
      if (accessToken && expiresAt) {
        const now = new Date().getTime();
        return now < parseInt(expiresAt, 10);
      }
      return false;
    } catch (e) {
      console.error("Error checking manual auth:", e);
      return false;
    }
  };
  
  return (
    <div className="container my-4">
      <div className="row">
        <div className="col-12">
          {/* Show loading while auth is being checked */}
          {!authChecked || (!authDisabled && inProgress === InteractionStatus.Login) ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Verifying authentication status...</p>
            </div>
          ) : (
            // If auth is disabled, always show the app. Otherwise check authentication
            (authDisabled || accounts.length > 0 || isManuallyAuthenticated()) ? (
              // User is authenticated or auth is disabled - show the application
              <AuthenticatedContent
                mode={mode}
                prompt={prompt}
                error={error}
                onVideoSelect={handleVideoSelect}
                onModeChange={setMode}
                onPromptChange={setPrompt}
                uploadQueue={uploadQueue}
                isProcessingQueue={isProcessingQueue}
                onRemoveFromQueue={handleRemoveFromQueue}
                onClearQueue={handleClearQueue}
                onCancelProcessing={handleCancelProcessing}
                onRetryProcessing={handleRetryProcessing}
                // New props for dual mode support
                processingMode={processingMode}
                onProcessingModeChange={setProcessingMode}
                batchQueue={batchQueue}
                batchResult={batchResult}
                onStartSingleVideoProcessing={startSingleVideoProcessing}
                onMoveBatchItem={moveBatchItem}
                onRemoveFromBatch={removeFromBatch}
                onProcessBatch={processBatch}
              />
            ) : (
              // User is not authenticated - show login
              <Login />
            )
          )}
        </div>
      </div>
    </div>
  );
}

// Main App component that decides which version to use
function App() {
  const authDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true';
  
  if (authDisabled) {
    return <AppWithoutAuth />;
  } else {
    return <AppWithAuth />;
  }
}

export default App;